import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { useAuthStore } from "@/stores/auth.store";
import type { AppTaskId } from "@shared";

// ═══════════════════════════════════════════════════════════
// Settings Hooks — TanStack Query wrappers for /api/admin/settings
// ═══════════════════════════════════════════════════════════

export interface AppSettings {
  baseCurrency: string;
  defaultCommissionBasis: string;
  fiscalPeriodType: string;
  clawbackWindowDays: number;
  maxCommissionCap: number | null;
  installedAt: number;
  subscriptionStatus: string;
  trialExpiresAt: number | null;
  registeredEvents: string[];
  defaultNotifyTemplate: string | null;
  /** Admin-configured default body template for the CRM Timeline Log action. NULL = built-in fallback. */
  defaultTimelineLogTemplate: string | null;
  /** Data Storage schema version last successfully provisioned. NULL = unknown / out of date. */
  storageSchemaVersion: number | null;
  /** Task IDs that are registered but not yet completed for this tenant. */
  pendingTaskIds: AppTaskId[];
}

export interface SettingsUpdate {
  baseCurrency?: string;
  defaultCommissionBasis?: string;
  fiscalPeriodType?: string;
  clawbackWindowDays?: number;
  maxCommissionCap?: number | null;
  defaultNotifyTemplate?: string | null;
  defaultTimelineLogTemplate?: string | null;
}

export function useSettings() {
  const isAdmin = useAuthStore((s) => s.isAdmin);
  return useQuery({
    queryKey: queryKeys.settings,
    queryFn: () => api.get<AppSettings>("/api/admin/settings"),
    enabled: !!isAdmin,
    staleTime: 60_000, // Settings change rarely
  });
}

export function useUpdateSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SettingsUpdate) => api.put<AppSettings>("/api/admin/settings", data),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.settings, data);
    },
  });
}
